import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, Loader2, RefreshCw, Wrench } from 'lucide-react'
import api from '../../utils/api'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getTodayDateText() {
  return formatDateText(new Date())
}

function getYesterdayDateText() {
  return addDays(getTodayDateText(), -1)
}

const CATEGORY_ORDER = ['refund-sensitive', 'manual-rebuild']

function getTaskCategoryName(task) {
  return task?.category_name || '其他任务'
}

function getTaskResultMessage(taskName, message) {
  return message || `${taskName}已执行完成`
}

export default function ReportGenerationTab() {
  const today = useMemo(() => getTodayDateText(), [])
  const yesterday = useMemo(() => getYesterdayDateText(), [])
  const [startDate, setStartDate] = useState(yesterday)
  const [endDate, setEndDate] = useState(yesterday)
  const [tasks, setTasks] = useState([])
  const [loadingTasks, setLoadingTasks] = useState(true)
  const [runningTaskKey, setRunningTaskKey] = useState('')
  const [runningBatch, setRunningBatch] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [taskResults, setTaskResults] = useState({})

  useEffect(() => {
    fetchTasks()
  }, [])

  const handleQuickRange = (days) => {
    const nextEndDate = today
    const nextStartDate = addDays(today, -(days - 1))
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
  }

  const groupedTasks = useMemo(() => {
    const categoryMap = new Map()

    CATEGORY_ORDER.forEach((key) => {
      categoryMap.set(key, {
        key,
        name: '',
        tasks: []
      })
    })

    tasks.forEach((task) => {
      const categoryKey = task?.category_key || 'other'
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, {
          key: categoryKey,
          name: getTaskCategoryName(task),
          tasks: []
        })
      }

      const current = categoryMap.get(categoryKey)
      current.name = current.name || getTaskCategoryName(task)
      current.tasks.push(task)
    })

    return Array.from(categoryMap.values()).filter((item) => item.tasks.length > 0)
  }, [tasks])

  const fetchTasks = async () => {
    setLoadingTasks(true)
    setErrorMsg('')

    try {
      const response = await api.get('/admin/report-generation/tasks')
      setTasks(response.data?.list || [])
    } catch (error) {
      console.error('获取报表生成任务失败:', error)
      setErrorMsg(error?.response?.data?.error || '获取报表生成任务失败，请稍后重试')
    } finally {
      setLoadingTasks(false)
    }
  }

  const handleRunTask = async (task, payload, fallbackMessage) => {
    setRunningTaskKey(task.task_key)
    setErrorMsg('')
    setSuccessMsg('')

    try {
      const response = await api.post('/admin/report-generation/run', {
        task_key: task.task_key,
        ...payload
      })
      const result = response.data || null
      setTaskResults((prev) => ({
        ...prev,
        [task.task_key]: result
      }))
      setSuccessMsg(getTaskResultMessage(task.task_name, response.data?.message || fallbackMessage))
    } catch (error) {
      console.error(`执行任务失败: ${task.task_key}`, error)
      setErrorMsg(error?.response?.data?.error || `${task.task_name}执行失败，请稍后重试`)
    } finally {
      setRunningTaskKey('')
    }
  }

  const handleRunYesterday = async (task) => {
    await handleRunTask(task, { mode: 'yesterday' }, `${task.task_name}已按昨日执行完成`)
  }

  const handleRunRange = async (task) => {
    await handleRunTask(
      task,
      {
        start_date: startDate,
        end_date: endDate
      },
      `${task.task_name}已按日期范围执行完成`
    )
  }

  const handleRunRefundSensitiveBatch = async () => {
    setRunningBatch(true)
    setErrorMsg('')
    setSuccessMsg('')

    try {
      const response = await api.post('/admin/report-generation/run-refund-sensitive-yesterday')
      const results = response.data?.results || []
      const nextTaskResults = {}
      results.forEach((item) => {
        if (item?.task_key) {
          nextTaskResults[item.task_key] = item
        }
      })
      setTaskResults((prev) => ({
        ...prev,
        ...nextTaskResults
      }))
      setSuccessMsg(response.data?.message || '退款敏感日报已执行完成')
    } catch (error) {
      console.error('执行退款敏感日报失败:', error)
      setErrorMsg(error?.response?.data?.error || '执行退款敏感日报失败，请稍后重试')
    } finally {
      setRunningBatch(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">报表生成(猛男)</h2>
        <p className="mt-1 text-sm text-gray-500">集中补跑退款敏感日报与手工重建任务，仅新增独立任务接口，不影响现有查询接口。</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 shadow-sm">
        “重跑昨日退款敏感日报”只会串行执行“昨日充值报表统计”和“昨日核心数据汇总”两个任务；下方会单独展示全部 3 个任务卡片。
      </div>

      {!loadingTasks && groupedTasks.map((group) => (
        <div key={group.key} className="space-y-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{group.name}</h3>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {group.tasks.map((task) => {
              const taskResult = taskResults[task.task_key]
              const isRunning = runningTaskKey === task.task_key

              return (
                <div key={task.task_key} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-bold text-gray-900">{task.task_name}</div>
                      <div className="mt-1 text-sm text-gray-500">{task.description}</div>
                      {task.report_tab_name ? (
                        <div className="mt-2 text-xs text-gray-400">对应左侧页面：{task.report_tab_name}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleRunYesterday(task)}
                      disabled={isRunning || runningBatch}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                      生成昨日
                    </button>
                    {task.supports_range ? (
                      <button
                        type="button"
                        onClick={() => handleRunRange(task)}
                        disabled={isRunning || runningBatch}
                        className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        按日期重跑
                      </button>
                    ) : null}
                  </div>

                  {taskResult ? (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <div className="mb-3 text-sm font-medium text-gray-900">最近执行结果</div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {(taskResult.summary_items || []).map((item, index) => (
                          <div key={`${task.task_key}-${index}`} className="rounded-lg bg-gray-50 px-4 py-3">
                            <div className="text-xs text-gray-500">{item.label}</div>
                            <div className="mt-1 text-base font-bold text-gray-900">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-900">退款敏感日报</div>
              <div className="mt-1 text-sm text-gray-500">按固定顺序执行充值报表统计和核心数据汇总，减少退款后口径不一致。</div>
            </div>
            <button
              type="button"
              onClick={handleRunRefundSensitiveBatch}
              disabled={runningBatch || loadingTasks}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {runningBatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
              重跑昨日退款敏感日报
            </button>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-3 text-sm font-medium text-gray-900">按日期范围执行</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <CalendarDays className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-0 bg-transparent text-sm text-gray-700 outline-none"
                />
              </div>

              <span className="text-sm text-gray-400">至</span>

              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <CalendarDays className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-0 bg-transparent text-sm text-gray-700 outline-none"
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setStartDate(yesterday)
                  setEndDate(yesterday)
                }}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                昨日
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(today)
                  setEndDate(today)
                }}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                今日
              </button>
              <button
                type="button"
                onClick={() => handleQuickRange(7)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                最近7天
              </button>
              <button
                type="button"
                onClick={() => handleQuickRange(30)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                最近30天
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(yesterday)
                  setEndDate(yesterday)
                  setErrorMsg('')
                  setSuccessMsg('')
                  setTaskResults({})
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4" />
                重置
              </button>
            </div>
          </div>
        </div>
      </div>

      {errorMsg ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
          {errorMsg}
        </div>
      ) : null}

      {successMsg ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-700 shadow-sm">
          {successMsg}
        </div>
      ) : null}

      {loadingTasks ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            正在加载任务列表...
          </div>
        </div>
      ) : null}
    </div>
  )
}
